import React, { useMemo, useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useElements, useStripe, CardElement } from "@stripe/react-stripe-js";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { Paper, Table, TableBody, TableCell, TableContainer, TableRow, CircularProgress } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "pages/components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/DashboardNavbar";
import EnhancedTableHead from "pages/components/common/EnhancedTableHead";
import CheckoutComponent from "pages/components/payments/CheckoutComponent";
import PageLayout from "pages/components/LayoutContainers/PageLayout";
import { UserQuotationApiService } from "api/userQuotation.api";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);
const TAX_RATE = 0.06;
const TAX_STATE = "Pennsylvania";

const validationSchema = Yup.object({
  firstName: Yup.string().required("This field is required"),
  lastName: Yup.string().required("This field is required"),
  addressLineOne: Yup.string().required("This field is required"),
  addressLineTwo: Yup.string().nullable(),
  city: Yup.string().required("This field is required"),
  state: Yup.string().required("This field is required"),
  country: Yup.string().required("This field is required"),
  zipCode: Yup.string().required("This field is required"),
  useBillingAddress: Yup.boolean().required(),
  shippingFirstName: Yup.string().when("useBillingAddress", {
    is: false,
    then: (s) => s.required("This field is required"),
    otherwise: (s) => s.notRequired(),
  }),
  shippingLastName: Yup.string().when("useBillingAddress", {
    is: false,
    then: (s) => s.required("This field is required"),
    otherwise: (s) => s.notRequired(),
  }),
  shippingAddressLineOne: Yup.string().when("useBillingAddress", {
    is: false,
    then: (s) => s.required("This field is required"),
    otherwise: (s) => s.notRequired(),
  }),
  shippingAddressLineTwo: Yup.string().nullable(),
  shippingCity: Yup.string().when("useBillingAddress", {
    is: false,
    then: (s) => s.required("This field is required"),
    otherwise: (s) => s.notRequired(),
  }),
  shippingState: Yup.string().when("useBillingAddress", {
    is: false,
    then: (s) => s.required("This field is required"),
    otherwise: (s) => s.notRequired(),
  }),
  shippingCountry: Yup.string().when("useBillingAddress", {
    is: false,
    then: (s) => s.required("This field is required"),
    otherwise: (s) => s.notRequired(),
  }),
  shippingZipCode: Yup.string().when("useBillingAddress", {
    is: false,
    then: (s) => s.required("This field is required"),
    otherwise: (s) => s.notRequired(),
  }),
});

const QuoteOnlineCheckoutContent = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initPage = async () => {
      try {
        setFetching(true);
        const quoteResp = await UserQuotationApiService.getQuotation(quoteId);
        setQuote(quoteResp.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to initialize checkout session.");
      } finally {
        setFetching(false);
      }
    };
    initPage();
  }, [quoteId]);

  const quoteProducts = quote?.items || [];
  const subTotal = useMemo(() => Number(quote?.amount || 0), [quote]);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      addressLineOne: "",
      addressLineTwo: "",
      city: "",
      state: "",
      country: "United States",
      zipCode: "",
      useBillingAddress: true,
      shippingFirstName: "",
      shippingLastName: "",
      shippingAddressLineOne: "",
      shippingAddressLineTwo: "",
      shippingCity: "",
      shippingState: "",
      shippingCountry: "United States",
      shippingZipCode: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!stripe || !elements) {
        toast.error("Stripe is not ready yet. Please try again.");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // 1. Create Payment Method
        const cardElement = elements.getElement(CardElement);
        const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: `${values.firstName} ${values.lastName}`,
            address: {
              line1: values.addressLineOne,
              city: values.city,
              state: values.state,
              postal_code: values.zipCode,
              country: "US",
            },
          },
        });

        if (pmError) {
          setError(pmError.message);
          return;
        }

        // 2. Prepare Checkout Data
        const checkoutData = {
          paymentMethodId: paymentMethod.id,
          firstName: values.firstName,
          lastName: values.lastName,
          addressLineOne: values.addressLineOne,
          addressLineTwo: values.addressLineTwo,
          city: values.city,
          state: values.state,
          country: values.country,
          zipCode: values.zipCode,
          useBillingAddress: values.useBillingAddress,
          shippingFirstName: values.shippingFirstName,
          shippingLastName: values.shippingLastName,
          shippingAddressLineOne: values.shippingAddressLineOne,
          shippingAddressLineTwo: values.shippingAddressLineTwo,
          shippingCity: values.shippingCity,
          shippingState: values.shippingState,
          shippingCountry: values.shippingCountry,
          shippingZipCode: values.shippingZipCode,
        };

        // 3. Backend Call
        const response = await UserQuotationApiService.checkout(quoteId, checkoutData);
        const result = response.data.data;

        if (result.success) {
          toast.success("Payment successful!");
          navigate("/user/quotations/listing");
        } else if (result.clientSecret) {
          // Handle 3DS if required
          const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(result.clientSecret);
          if (confirmError) {
             setError(confirmError.message);
          } else if (paymentIntent.status === "succeeded") {
             toast.success("Payment successful!");
             navigate("/user/quotations/listing");
          } else {
             setError("Payment requires further action.");
          }
        } else {
          setError(result.message || "Payment failed");
        }
      } catch (checkoutError) {
        console.error(checkoutError);
        toast.error(checkoutError?.response?.data?.message || checkoutError?.message || "Payment failed");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleShippingAddressChange = (event) => {
    const checked = event.target.checked;
    formik.setFieldValue("useBillingAddress", checked);
    if (checked) {
      formik.setFieldValue("shippingFirstName", formik.values.firstName);
      formik.setFieldValue("shippingLastName", formik.values.lastName);
      formik.setFieldValue("shippingAddressLineOne", formik.values.addressLineOne);
      formik.setFieldValue("shippingAddressLineTwo", formik.values.addressLineTwo);
      formik.setFieldValue("shippingCity", formik.values.city);
      formik.setFieldValue("shippingState", formik.values.state);
      formik.setFieldValue("shippingCountry", formik.values.country);
      formik.setFieldValue("shippingZipCode", formik.values.zipCode);
      return;
    }

    formik.setFieldValue("shippingFirstName", "");
    formik.setFieldValue("shippingLastName", "");
    formik.setFieldValue("shippingAddressLineOne", "");
    formik.setFieldValue("shippingAddressLineTwo", "");
    formik.setFieldValue("shippingCity", "");
    formik.setFieldValue("shippingState", "");
    formik.setFieldValue("shippingCountry", "United States");
    formik.setFieldValue("shippingZipCode", "");
  };

  if (fetching) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3} display="flex" justifyContent="center">
          <CircularProgress />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!quote) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <Card sx={{ p: 3 }}>
            <SoftTypography variant="h5">Quote not found or session error</SoftTypography>
          </Card>
        </SoftBox>
      </DashboardLayout>
    );
  }

  const shippingState = formik.values.useBillingAddress ? formik.values.state : formik.values.shippingState;
  const tax = shippingState?.toLowerCase() === TAX_STATE.toLowerCase() ? subTotal * TAX_RATE : 0;
  const finalTotal = subTotal + tax;

  return (
    <PageLayout>
      <SoftBox p={2} component="form" role="form" onSubmit={formik.handleSubmit}>
        <Grid container spacing={3} justifyContent="center" mt={0.5}>
          <Grid item xs={12} display="flex" justifyContent="center">
            <SoftTypography variant="h3" fontSize="larger" mb={2} mt={2}>
              Quote Online Checkout
            </SoftTypography>
          </Grid>

          <Grid item xs={11} md={6} lg={5}>
            <Card sx={{ padding: 3 }}>
              <SoftBox mb={2}>
                <SoftTypography variant="h5" fontWeight="medium" mb={1}>
                  What&apos;s included
                </SoftTypography>
                <SoftTypography variant="button" color="text">
                  Quote #{quoteId} for {quote.customerName || "Customer"}
                </SoftTypography>
              </SoftBox>
              <TableContainer component={Paper} sx={{ boxShadow: "none"}}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <EnhancedTableHead headCells={headCells} />
                  <TableBody>
                    {quoteProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <SoftTypography variant="body2">{product.name}</SoftTypography>
                        </TableCell>
                        <TableCell>
                          <SoftTypography variant="body2">{product.quantity || 1}</SoftTypography>
                        </TableCell>
                        <TableCell>
                          <SoftTypography variant="body2">
                            ${Number(product.rate || 0).toFixed(2)}
                          </SoftTypography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <SoftBox
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-start",
                  textAlign: "right",
                }}
                mr={1}
                mt={2}
              >
                <SoftBox>
                  <SoftTypography variant="h6" fontSize={16} mt={1}>
                    <SoftTypography variant="b" mr={1}>
                      Sub Total:
                    </SoftTypography>
                    ${subTotal.toFixed(2)}
                  </SoftTypography>
                  {tax > 0 && (
                    <SoftTypography variant="h6" fontSize={16} mt={1}>
                      <SoftTypography variant="b" mr={1}>
                        Tax:
                      </SoftTypography>
                      ${tax.toFixed(2)}
                    </SoftTypography>
                  )}
                  <SoftTypography variant="h5" fontSize={18} mt={1}>
                    <SoftTypography variant="b" mr={1}>
                      Total:
                    </SoftTypography>
                    ${finalTotal.toFixed(2)}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>

          <CheckoutComponent
            error={error}
            formik={formik}
            handleShippingAddressChange={handleShippingAddressChange}
            options={{
              loading,
              buttonText: "Pay Quote",
            }}
          />
        </Grid>
      </SoftBox>
    </PageLayout>
  );
};

const QuoteOnlineCheckout = () => {
  return (
    <Elements stripe={stripePromise}>
      <QuoteOnlineCheckoutContent />
    </Elements>
  );
};

const headCells = [
  {
    id: "product",
    numeric: true,
    disablePadding: true,
    label: "Product",
    sortable: false,
  },
  {
    id: "quantity",
    numeric: true,
    disablePadding: true,
    label: "Quantity",
    sortable: false,
  },
  {
    id: "price",
    numeric: false,
    disablePadding: false,
    label: "Price (Per Unit)",
    sortable: false,
  },
];

export default QuoteOnlineCheckout;
